import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "app/components/ui/card";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "app/components/ui/chart";
import type { ChartConfig } from "app/components/ui/chart";
import { useV1UtilizationData, type V1UtilizationDataPoint } from "~/hooks/useV1UtilizationData";

const formatUSD = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};



const UtilizationChart = () => {
  const { data: chartData, isLoading, hasError, isEmpty } = useV1UtilizationData();

  if (isLoading) return null;
  if (hasError) return null;
  if (isEmpty) return null;

  // Create allData map for backwards compatibility with tooltip
  const allData = new Map<string, V1UtilizationDataPoint>();
  chartData.forEach((item) => {
    allData.set(item.date, item);
  });

  // Calculate total capacity from the latest data point
  const lastDataPoint = chartData[chartData.length - 1];
  const capacityRemaining = lastDataPoint
    ? lastDataPoint.sReserveInReserveBalance +
      lastDataPoint.treasurySReserveInReserveBalance +
      lastDataPoint.reserveBalance +
      lastDataPoint.treasuryReserveBalance
    : 0;

  const chartConfig = {
    totalPrincipalReceivables: {
      label: "Amount Borrowed",
      color: "hsl(var(--chart-1))",
    },
    totalInterestReceivables: {
      label: "Interest Due",
      color: "hsl(var(--chart-2))",
    },
  } satisfies ChartConfig;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Protocol Utilization</CardTitle>
        <div className="text-sm text-muted-foreground">
          Capacity Remaining: {formatUSD(capacityRemaining)}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ChartContainer config={chartConfig}>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart
              data={chartData}
              margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => value.split("-").slice(1).join("/")}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tickFormatter={formatUSD} />
              <ChartTooltip
                content={<ChartTooltipContent />}
                formatter={(value: number, name: string, props: any) => {
                  const date = props.payload.date;
                  const data = allData.get(date);
                  if (!data) return [formatUSD(value), name];

                  // Add breakdown by clearinghouse in tooltip
                  const breakdown = Object.entries(data.byAddress)
                    .map(([address, values]) => ({
                      address,
                      value:
                        name === "Amount Borrowed"
                          ? values.principalReceivables
                          : values.interestReceivables,
                    }))
                    .filter((item) => item.value > 0)
                    .sort((a, b) => b.value - a.value); // Sort by value, highest first

                  return [
                    <div className="space-y-2 mb-4 border-t border-white/10 pt-4 w-full">
                      {name}
                      <div className="font-medium">{formatUSD(value)}</div>
                      {breakdown.length > 0 && (
                        <div className="pt-2 space-y-1">
                          {breakdown.map(({ address, value }) => (
                            <div
                              key={address}
                              className="flex justify-between gap-4 text-xs"
                            >
                              <span className="text-muted-foreground font-mono">
                                {address.slice(0, 6)}...{address.slice(-4)}
                              </span>
                              <span className="font-medium tabular-nums">
                                {formatUSD(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>,
                  ];
                }}
              />
              <ChartLegend content={<ChartLegendContent />} />
              {Object.entries(chartConfig).map(([key, config]) => (
                <Line
                  key={key}
                  type="monotone"
                  name={config.label}
                  dataKey={key}
                  stroke={`var(--color-${key})`}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default UtilizationChart;
